'use strict';

import axios from 'axios';
import auth from './login';
import communications from './configuration/communications';
import contactList from './contactList';
import covidScreening from './covidScreening';
import establishment from './establishments';
import modules from './modules';
import my from './my';
import headOffices from './headOffices';
import places from './configuration/places';
import resources from './configuration/resources';
import schedules from './schedules';
import appointments from './appointments';
import journal from './journal';
import services from './services';
import settings from './settings';
import stats from './stats';
import texts from './texts';
import other from './other';
import forms from './forms';
import formsAH635 from './formsAH635';
import setOptions from './setOptions';
import notifications from './notifications';
import patients from './patients';
import { TrimozError } from '@trimoz/trimoz-vue-error-handling';
import { isAdminApp, isUsingKc, retrieveErrorInternalCode } from './utils';
import { JwtInterceptor } from './interceptors';
import { KeycloakAdapter } from './interceptors/keycloakAdapter/keycloakAdapter';
import axiosRetry from 'axios-retry';
import { get } from 'lodash';

export const convertApiErrorIntoTrimozError = (error) => {
    const contextCode = get(error, 'response.data.messages.errors[0].context.error_code', null);
    const internalCode = retrieveErrorInternalCode(error);

    return new TrimozError(error, 'errors.generic', internalCode, contextCode);
};

let keycloakAdapter = null;

const DEV_TIMEOUT = 60000;
const PROD_TIMEOUT = 20000;

export default (url, apiUser = true, mainProduct, Store) => {
    let options = {
        baseURL: url,
        headers: { 'PRODUCT': mainProduct },
        timeout: process.env.NODE_ENV === 'development' ? DEV_TIMEOUT : PROD_TIMEOUT,
        withCredentials: true,
        validateStatus: function(status) { return (status >= 200 && status < 300); }, // Status qui ne throw pas d'error
    };

    if (apiUser) {
        options.headers['X-TRIMOZ-ROLE'] = 'public';
    }

    const errorCallbacks = {
        onForbiddenError: (error) => { console.log('forbidden error', error); },
        onUnauthorizedError: (error) => { console.log('unauthorized error', error); },
        onSheddingError: (error) => { console.log('shedding error', error); },
    };

    let API = {
        url: url,
        http: axios.create(options),
        apiVersion: 'local',
        apiVersionDate: 'now',
        apiSecurityWhitelistingIp: null,
        wrapperVersion: '3.36.0',
        wrapperVersionDate: '2025-04-03',

        handleError: async(request) => {
            try {
                return await request;
            }
            catch (error) {
                if (error.code === 'ECONNABORTED') {
                    throw new TrimozError(error, 'errors.request.timeout');
                }
                else if (error.response) {
                    if (error.response.status === 403) {
                        errorCallbacks.onForbiddenError(error);
                    }
                    else if (error.response.status === 401) {
                        errorCallbacks.onUnauthorizedError(error);
                    }
                    else if (
                        retrieveErrorInternalCode(error) === 'service-unavailable.shedding-page'
                    ) {
                        errorCallbacks.onSheddingError(error);
                    }

                    throw convertApiErrorIntoTrimozError(error);
                }
                else {
                    throw error;
                }
            }
        },
    };

    axiosRetry(API.http, { retries: 2 });

    if (isUsingKc) {
        keycloakAdapter = new KeycloakAdapter();
        API.http.interceptors.request.use(keycloakAdapter.requestOnFulfilled, keycloakAdapter.requestOnRejected);
    }
    else {
        const jwtInterceptor = new JwtInterceptor(API, auth(API).auth.getAccessToken, Store);

        if (isAdminApp()) {
            API.http.interceptors.response.use(jwtInterceptor.responseOnFulfilled);
        }
        else {
            API.http.interceptors.request.use(jwtInterceptor.requestOnFulfilled, jwtInterceptor.requestOnRejected);
            API.http.interceptors.response.use(jwtInterceptor.responseOnFulfilled, jwtInterceptor.responseOnRejected);
        }
    }

    return Object.assign({},
        {
            errorCallbacks: errorCallbacks,
            apiVersion: () => API.apiVersion,
            apiVersionDate: () => API.apiVersionDate,
            apiSecurityWhitelistingIp: () => API.apiSecurityWhitelistingIp,
            wrapperVersion: API.wrapperVersion,
            wrapperVersionDate: API.wrapperVersionDate,
        },
        auth(API),
        communications(API),
        contactList(API),
        covidScreening(API),
        establishment(API),
        headOffices(API),
        places(API),
        resources(API),
        stats(API),
        schedules(API),
        appointments(API),
        journal(API),
        other(API),
        services(API),
        setOptions(API),
        settings(API),
        texts(API),
        modules(API),
        my(API),
        forms(API),
        formsAH635(API),
        notifications(API),
        patients(API),
    );
};

export {
    keycloakAdapter
};
